import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const GlobalAudioContext = createContext(null);

export const GlobalAudioProvider = ({ children }) => {
    const [isMuted, setIsMuted] = useState(() => localStorage.getItem('cabaza_audio_muted') === 'true');
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentUrl, setCurrentUrl] = useState(null);
    const [anchor, setAnchor] = useState(null); // 'bubble' | 'bottom-right' | 'nudge' | { top, left }

    const audioRef = useRef(null);
    const endHandlersRef = useRef([]);
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const sourceRef = useRef(null);
    const rafRef = useRef(null);
    const amplitudeSubsRef = useRef(new Set());

    const getAudio = useCallback(() => {
        if (!audioRef.current) {
            const audio = new Audio();
            audio.crossOrigin = 'anonymous';
            audio.addEventListener('ended', () => {
                setIsPlaying(false);
                endHandlersRef.current.forEach(h => { try { h(); } catch (e) { /* noop */ } });
                endHandlersRef.current = [];
            });
            audio.addEventListener('error', () => setIsPlaying(false));
            audioRef.current = audio;
        }
        return audioRef.current;
    }, []);

    // Initialise lazily le AudioContext + AnalyserNode au premier play
    const setupAnalyser = useCallback(() => {
        if (analyserRef.current) return;
        try {
            const AudioCtxCls = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtxCls) return;
            const audio = getAudio();
            audioCtxRef.current = new AudioCtxCls();
            const source = audioCtxRef.current.createMediaElementSource(audio);
            const analyser = audioCtxRef.current.createAnalyser();
            analyser.fftSize = 256;
            analyser.smoothingTimeConstant = 0.6;
            source.connect(analyser);
            analyser.connect(audioCtxRef.current.destination);
            sourceRef.current = source;
            analyserRef.current = analyser;
        } catch (err) {
            console.warn('[GlobalAudio] Web Audio API setup failed (lip-sync disabled):', err?.message || err);
            analyserRef.current = null;
        }
    }, [getAudio]);

    // Boucle rAF qui publie l'amplitude aux abonnés
    const tickAmplitude = useCallback(() => {
        if (!analyserRef.current || amplitudeSubsRef.current.size === 0) {
            rafRef.current = null;
            return;
        }
        const buffer = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteTimeDomainData(buffer);
        // Calcul amplitude RMS normalisée (0..1)
        let sum = 0;
        for (let i = 0; i < buffer.length; i++) {
            const v = (buffer[i] - 128) / 128;
            sum += v * v;
        }
        const rms = Math.sqrt(sum / buffer.length);
        const amp = Math.min(1, rms * 2.5);
        amplitudeSubsRef.current.forEach(fn => {
            try { fn(amp); } catch { /* noop */ }
        });
        rafRef.current = requestAnimationFrame(tickAmplitude);
    }, []);

    const subscribeAmplitude = useCallback((fn) => {
        amplitudeSubsRef.current.add(fn);
        if (!rafRef.current && analyserRef.current) {
            rafRef.current = requestAnimationFrame(tickAmplitude);
        }
        return () => {
            amplitudeSubsRef.current.delete(fn);
        };
    }, [tickAmplitude]);

    const playAudio = useCallback(async (url, options = {}) => {
        if (isMuted || !url) return;
        const audio = getAudio();
        setupAnalyser();
        if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
            try { await audioCtxRef.current.resume(); } catch { /* noop */ }
        }
        audio.pause();
        audio.src = url;
        audio.currentTime = 0;
        setCurrentUrl(url);
        setAnchor(options.anchor || 'bottom-right');
        endHandlersRef.current = [];
        if (options.onEnd) endHandlersRef.current.push(options.onEnd);
        try {
            await audio.play();
            setIsPlaying(true);
            if (!rafRef.current && analyserRef.current) {
                rafRef.current = requestAnimationFrame(tickAmplitude);
            }
        } catch (err) {
            console.warn('[GlobalAudio] play error:', err?.message || err);
            setIsPlaying(false);
        }
    }, [isMuted, getAudio, setupAnalyser, tickAmplitude]);

    const stopAudio = useCallback(() => {
        const audio = getAudio();
        audio.pause();
        audio.currentTime = 0;
        setIsPlaying(false);
        setCurrentUrl(null);
        setAnchor(null);
        endHandlersRef.current = [];
    }, [getAudio]);

    const toggleMute = useCallback(() => {
        setIsMuted(prev => {
            const next = !prev;
            localStorage.setItem('cabaza_audio_muted', next.toString());
            if (next && audioRef.current) {
                audioRef.current.pause();
                setIsPlaying(false);
            }
            return next;
        });
    }, []);

    useEffect(() => () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        if (audioCtxRef.current) {
            try { audioCtxRef.current.close(); } catch { /* noop */ }
            audioCtxRef.current = null;
        }
    }, []);

    const value = {
        isMuted,
        isPlaying,
        currentUrl,
        anchor,
        playAudio,
        stopAudio,
        toggleMute,
        subscribeAmplitude
    };

    return <GlobalAudioContext.Provider value={value}>{children}</GlobalAudioContext.Provider>;
};

export const useGlobalAudio = () => {
    const ctx = useContext(GlobalAudioContext);
    if (!ctx) throw new Error('useGlobalAudio must be used within a GlobalAudioProvider');
    return ctx;
};
